/**
 * Smart Portfolios (docs/proposals/03-smart-portfolios.md): themed baskets of instruments with target
 * weights, run by the investment team. Each investment is an isolated 'portfolio' sub-account holding
 * long 1x positions; a rebalance brings every investment back to the same target weights, so investors
 * who joined at different times hold the same weights, not the same quantities. All amounts are USDT.
 */
import { D } from './decimal.js';
export const SMART_LIMITS = {
    /** Default minimum to invest (each portfolio sets its own). */
    minInvestment: '500',
    /** Default maximum per investment, and the most a portfolio may set. */
    maxInvestment: '250000',
    maxInvestmentCeiling: '1000000',
    /** Default cap on the money in one portfolio (all investors), so house exposure grows only when an admin allows it. */
    aumCap: '2000000',
    maxRunningInvestments: 20,
    defaultStopLossPct: '0.4',
    minStopLossPct: '0.05',
    maxStopLossPct: '0.95',
    /** Warn the investor when this share of the allowed loss is used up. */
    stopLossWarnAt: '0.75',
    /** Kept as cash so opening fees and spread never make a buy fail. */
    cashBuffer: '0.005',
    /** A leg is traded only when it moves at least this much, and at least this share of equity. */
    minTrade: '10',
    minLegShare: '0.01',
    /** A run stops when more than this share of its legs fail (with at least 10 legs). */
    abortFailedShare: '0.1',
    /** A leg waits for a better price when the spread is wider than this share of the price. */
    maxSpread: '0.01',
    /** One drift rebalance per investment per this many hours at most. */
    driftCooldownHours: 24,
    minComponents: 2,
    maxComponents: 25,
    /** No holding below 1 % or above 60 % of the portfolio. */
    minWeightBps: 100,
    maxWeightBps: 6000,
};
export const PORTFOLIO_CATEGORIES = [
    { id: 'crypto', label: 'Crypto' },
    { id: 'stocks', label: 'Stocks' },
    { id: 'commodities', label: 'Commodities' },
    { id: 'indices', label: 'Indices' },
    { id: 'forex', label: 'Forex' },
    { id: 'mixed', label: 'Mixed' },
];
// ---------- lock terms ----------
/** Lock lengths a portfolio can offer, in days. 0 is Flexible (no lock). */
export const LOCK_DAYS = [0, 90, 180, 365];
export const LOCK_DEFAULTS = {
    terms: [
        { days: 90, bonusBps: 50, exitFeeBps: 100 },
        { days: 180, bonusBps: 150, exitFeeBps: 150 },
        { days: 365, bonusBps: 400, exitFeeBps: 200 },
    ],
    /** Leaving within this many days of investing is free. */
    coolingOffDays: 14,
    bonusCap: '5000',
    maxBonusBps: 1000,
    maxExitFeeBps: 500,
    maxCoolingOffDays: 30,
    /** Reminders before a lock ends, in days. */
    remindDays: [7, 1],
};
export const lockLabel = (days) => (days === 0 ? 'Flexible' : days === 365 ? '12 months' : `${Math.round(days / 30)} months`);
/** Problem with a portfolio's lock terms, or null when they are fine. */
export function lockTermsProblem(terms) {
    if (!Array.isArray(terms))
        return 'lockTerms must be a list';
    const seen = new Set();
    for (const t of terms) {
        if (!t || !LOCK_DAYS.includes(t.days) || t.days === 0)
            return 'Each lock term is 90, 180 or 365 days';
        if (seen.has(t.days))
            return `${lockLabel(t.days)} is listed twice`;
        seen.add(t.days);
        if (!Number.isInteger(t.bonusBps) || t.bonusBps < 0 || t.bonusBps > LOCK_DEFAULTS.maxBonusBps)
            return `${lockLabel(t.days)}: bonus must be 0–${LOCK_DEFAULTS.maxBonusBps / 100}%`;
        if (!Number.isInteger(t.exitFeeBps) || t.exitFeeBps < 0 || t.exitFeeBps > LOCK_DEFAULTS.maxExitFeeBps)
            return `${lockLabel(t.days)}: early-exit fee must be 0–${LOCK_DEFAULTS.maxExitFeeBps / 100}%`;
    }
    return null;
}
/**
 * The bonus for a lock: the average amount put in over the term (principal-days ÷ term days) × the
 * bonus rate, capped. Profit and loss do not count, so money added late earns only its share.
 */
export function lockBonus(principalDays, termDays, bonusBps, cap) {
    if (termDays <= 0 || bonusBps <= 0)
        return D.ZERO;
    const v = D.from(principalDays).div(termDays, 'down').mul(bonusBps).div(10000, 'down').round(2, 'down');
    return D.max(D.ZERO, D.min(v, cap));
}
/** Principal-days after holding `invested` from `from` to `to` (only time inside the lock counts). */
export function accruePrincipal(principalDays, invested, from, to, lockedUntil) {
    const end = lockedUntil && lockedUntil < to ? lockedUntil : to;
    const days = Math.max(0, end.getTime() - from.getTime()) / 86_400_000;
    return D.from(principalDays).add(D.from(invested).mul(days.toFixed(8))).round(8, 'down');
}
/** The early-exit fee on a value, rounded up to the cent. */
export const exitFee = (value, feeBps) => (feeBps > 0 ? D.max(D.ZERO, D.from(value)).mul(feeBps).div(10000, 'up').round(2, 'up') : D.ZERO);
/** Where an investment stands in its lock at `now`. */
export function lockStateAt(now, lockedUntil, coolingOffUntil) {
    if (!lockedUntil || lockedUntil <= now)
        return 'flexible';
    if (coolingOffUntil && now < coolingOffUntil)
        return 'cooling_off';
    return 'locked';
}
/** Problem with a set of weights, or null when they are fine. */
export function weightsProblem(components) {
    if (!Array.isArray(components))
        return 'components must be a list';
    if (components.length < SMART_LIMITS.minComponents)
        return `A portfolio needs at least ${SMART_LIMITS.minComponents} holdings`;
    if (components.length > SMART_LIMITS.maxComponents)
        return `A portfolio can have at most ${SMART_LIMITS.maxComponents} holdings`;
    const seen = new Set();
    let sum = 0;
    for (const c of components) {
        if (!c || typeof c.symbol !== 'string' || !c.symbol)
            return 'Every holding needs an instrument';
        if (seen.has(c.symbol))
            return `${c.symbol} is listed twice`;
        seen.add(c.symbol);
        if (!Number.isInteger(c.weightBps))
            return `${c.symbol}: weight must be a whole number of basis points`;
        if (c.weightBps < SMART_LIMITS.minWeightBps || c.weightBps > SMART_LIMITS.maxWeightBps) {
            return `${c.symbol}: weight must be between ${SMART_LIMITS.minWeightBps / 100}% and ${SMART_LIMITS.maxWeightBps / 100}%`;
        }
        sum += c.weightBps;
    }
    if (sum !== 10000)
        return `Weights add up to ${(sum / 100).toFixed(2)}%, not 100%`;
    return null;
}
/** Equal weights in basis points; the remainder goes to the first holdings, one bp each. */
export function equalWeights(symbols) {
    if (!symbols.length)
        return [];
    const base = Math.floor(10000 / symbols.length);
    let left = 10000 - base * symbols.length;
    return symbols.map((symbol) => ({ symbol, weightBps: base + (left-- > 0 ? 1 : 0) }));
}
/**
 * What to trade to bring an investment back to its target weights: target_i = (equity − buffer) × w_i,
 * delta_i = target_i − current_i. Only deltas of at least max(minTrade, minLegShare × equity) trade,
 * except holdings no longer in the portfolio, which are always sold in full. Reduces come first
 * (they free the cash the opens need).
 */
export function planRebalance(p) {
    const equity = D.from(p.equity);
    if (!equity.isPos())
        return [];
    const investable = equity.mul(D.ONE.sub(p.cashBuffer ?? SMART_LIMITS.cashBuffer), 'down');
    const threshold = D.max(p.minTrade ?? SMART_LIMITS.minTrade, equity.mul(p.minLegShare ?? SMART_LIMITS.minLegShare));
    const current = new Map();
    for (const h of p.holdings)
        current.set(h.symbol, (current.get(h.symbol) ?? D.ZERO).add(h.value));
    const target = new Map(p.targets.map((t) => [t.symbol, investable.mul(t.weightBps).div(10000, 'down').round(2, 'down')]));
    const reduces = [];
    const opens = [];
    for (const [symbol, cur] of current) {
        if (!cur.isPos())
            continue;
        const t = target.get(symbol);
        if (t === undefined) {
            reduces.push({ symbol, action: 'reduce', notional: cur.round(2, 'down'), fraction: D.ONE });
            continue;
        }
        const delta = t.sub(cur);
        if (delta.isNeg() && delta.abs().gte(threshold)) {
            const fraction = D.min(D.ONE, delta.abs().div(cur, 'down').round(8, 'down'));
            reduces.push({ symbol, action: 'reduce', notional: delta.abs().round(2, 'down'), fraction });
        }
    }
    for (const [symbol, t] of target) {
        const delta = t.sub(current.get(symbol) ?? D.ZERO).round(2, 'down');
        if (delta.isPos() && delta.gte(threshold))
            opens.push({ symbol, action: 'open', notional: delta });
    }
    const bySize = (a, b) => b.notional.cmp(a.notional);
    return [...reduces.sort(bySize), ...opens.sort(bySize)];
}
/** The largest gap between a holding's weight and its target, in percentage points. */
export function maxDriftPp(equity, holdings, targets) {
    const eq = D.from(equity);
    if (!eq.isPos())
        return 0;
    const cur = new Map();
    for (const h of holdings)
        cur.set(h.symbol, (cur.get(h.symbol) ?? D.ZERO).add(h.value));
    let worst = 0;
    const symbols = new Set([...cur.keys(), ...targets.map((t) => t.symbol)]);
    for (const s of symbols) {
        const w = (cur.get(s) ?? D.ZERO).div(eq).toNumber() * 100;
        const t = (targets.find((x) => x.symbol === s)?.weightBps ?? 0) / 100;
        worst = Math.max(worst, Math.abs(w - t));
    }
    return worst;
}
/** The next scheduled rebalance after `from`: day N of the month (quarterly: Jan/Apr/Jul/Oct) at 10:00 UTC. */
export function nextRebalanceAt(schedule, day, from) {
    if (schedule === 'manual')
        return null;
    const step = schedule === 'quarterly' ? 3 : 1;
    let y = from.getUTCFullYear();
    let m = from.getUTCMonth();
    if (step === 3)
        m -= m % 3;
    for (let i = 0; i < 6; i++) {
        const at = new Date(Date.UTC(y, m, day, 10, 0, 0));
        if (at.getTime() > from.getTime())
            return at;
        m += step;
        if (m > 11) {
            m -= 12;
            y++;
        }
    }
    return null;
}
/** Human wording for why a rebalance leg did not trade. */
export const REBALANCE_LEG_REASONS = {
    market_closed: 'Market was closed; it trades at the next open',
    below_min: 'Too small to trade at this amount',
    insufficient_funds: 'Not enough cash in the portfolio',
    stale_price: 'No fresh price',
    wide_spread: 'Spread too wide; it trades when prices settle',
    instrument_disabled: 'Instrument not available',
    trading_restricted: 'Trading is restricted on the account',
    rejected: 'Not accepted',
    error: 'Failed; retried at the next check',
};
/** The equity at which an investment closes: invested × (1 − stop loss %). */
export const investmentStopLevel = (invested, pct) => D.from(invested).mul(D.ONE.sub(pct)).round(8, 'down');
//# sourceMappingURL=smart-portfolios.js.map